import BaseLayout from '@/layout/BaseLayout';
import Dropzone from '@shelflix/ui-web/components/Dropzone/index';
import { Button } from '@shelflix/ui-web/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@shelflix/ui-web/components/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  scrollToError,
} from '@shelflix/ui-web/components/ui/form';
import { Input } from '@shelflix/ui-web/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shelflix/ui-web/components/ui/select';
import { useForm } from 'react-hook-form';

import { useBrandFormStore } from '@/screens/BrandCreate/store';
import { trpc } from '@/trpcClient';
import { checkBrandCreateInput, type CreateBrandInputType } from '@shelflix/schemas/brand/create';
import Icon from '@shelflix/ui-web/components/Icon';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ColorInput } from '@/components/ColorInput';
import { MultiSelect } from '@/components/MultiSelect';
import { usePreventLeave } from '@/utils/usePreventLeave';
import { allowedColors } from '@shelflix/schemas/_common/validation';
import { EBrandCategory, EBrandCategoryDisplayName } from '@shelflix/schemas/brand/enums';
import { toast } from 'sonner';
import { logger } from '@/utils/logger';

/**
 * Convert a File to base64 string (without data URL prefix)
 */
async function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // Remove data URL prefix (e.g., "data:image/png;base64,")
      const base64 = result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export const useListBrands = (search?: string) => {
  return useQuery({
    ...trpc.brand.list.queryOptions({
      search: { fields: ['name', 'name'], value: search ?? '' },
    }),
    enabled: !!search,
    refetchOnWindowFocus: false,
  });
};

export function BrandCreate() {
  const { formData, resetForm } = useBrandFormStore();
  const createBrandMutation = useMutation(trpc.brand.create.mutationOptions());
  const uploadImageMutation = useMutation(trpc.image.uploadCompressed.mutationOptions());
  const [thumbnail, setThumbnail] = useState<File | null>(null);

  const [isUploading, setIsUploading] = useState(false);

  const navigate = useNavigate();

  const form = useForm<CreateBrandInputType & { active: boolean }>({
    defaultValues: { ...formData },
    mode: 'all',
  });

  const onSubmit = async (data: CreateBrandInputType) => {
    if (createBrandMutation.isPending || uploadImageMutation.isPending) return;

    try {
      setIsUploading(true);

      // First, upload the image with compression
      let imageKey: string | undefined;
      if (thumbnail) {
        const base64Data = await fileToBase64(thumbnail);
        const uploadResult = await uploadImageMutation.mutateAsync({
          base64Data,
          filename: thumbnail.name,
          imageType: 'brand-logo',
        });
        imageKey = uploadResult.key;
        logger.info('Image uploaded', {
          originalSize: uploadResult.originalSize,
          compressedSize: uploadResult.compressedSize,
          savingsPercent: uploadResult.savingsPercent,
        });
      }

      const payload = {
        ...data,
        code: data.code.toUpperCase(),
        imageKey, // Use the pre-uploaded key
        category: data.category,
        relatedBrands: data.relatedBrands ?? [],
      } as CreateBrandInputType;

      await createBrandMutation.mutateAsync(payload);
      resetForm();
      navigate('/brand', { state: { toastSuccess: true } });
    } catch (error) {
      toast.error(`Erro ao cadastrar marca: ${(error as Error).message}`);
    } finally {
      setIsUploading(false);
    }
  };

  usePreventLeave(isUploading);

  const dropzoneRef = useRef<HTMLDivElement>(null);

  const category = form.watch('category');
  const relatedBrands = form.watch('relatedBrands');

  const [search, setSearch] = useState<string | undefined>(undefined);
  const { data } = useListBrands(search);

  const brands = data?.brands ?? [];

  return (
    <BaseLayout>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit, scrollToError)} className="w-full">
          <div className="flex w-full flex-col">
            <section className="flex w-full flex-col gap-[28px] rounded-[5px] sm:flex-row">
              <Card className="order-2 rounded-[10px] sm:order-1 sm:w-[50%]">
                <CardHeader className="pb-0">
                  <CardTitle className="text-primary text-[18px] leading-5 font-semibold sm:text-2xl">
                    Carregar logo
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <FormField
                    control={form.control}
                    name="fileType"
                    rules={{ validate: (value) => checkBrandCreateInput('fileType', value) }}
                    render={({ field }) => (
                      <FormItem className="flex flex-col gap-[3px]">
                        <FormControl>
                          <Dropzone
                            variant="video"
                            removable={false}
                            onFilesSelected={(files) => {
                              setThumbnail(files[0]);
                              field.onChange(files[0]?.type);
                            }}
                            ref={dropzoneRef}
                            title="Arraste ou clique aqui para selecionar seu arquivo"
                            description="JPG, PNG ou SVG com fundo transparente (16:9)"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>
              <Card className="order-1 flex rounded-[10px] sm:order-2 sm:w-[50%]">
                <CardHeader className="pb-0">
                  <CardTitle className="text-primary text-[18px] leading-5 font-semibold sm:text-2xl">
                    Informações da marca
                  </CardTitle>
                </CardHeader>
                <CardContent className="flex flex-col gap-[27px_12px]">
                  <FormField
                    control={form.control}
                    name="name"
                    rules={{ validate: (value) => checkBrandCreateInput('name', value) }}
                    render={({ field }) => (
                      <FormItem className="flex flex-col gap-[3px]">
                        <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">Nome da Marca</FormLabel>
                        <FormControl>
                          <Input className="h-10" placeholder="Digite o nome da marca" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="code"
                    rules={{ validate: (value) => checkBrandCreateInput('code', value) }}
                    render={({ field }) => (
                      <FormItem className="flex flex-col gap-[3px]">
                        <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">
                          Identificador de 3 dígitos
                        </FormLabel>
                        <FormControl>
                          <Input className="h-10 uppercase" {...field} maxLength={3} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="active"
                    render={() => (
                      <FormItem className="flex flex-col gap-[3px]">
                        <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">Status</FormLabel>
                        <FormControl>
                          <Select value={'Ativo'} disabled={true}>
                            <SelectTrigger className="h-10 w-full">
                              <SelectValue placeholder="Selecione uma opção" />
                            </SelectTrigger>
                            <SelectContent className=" ">
                              <SelectItem value={'Ativo'}>Ativa</SelectItem>
                            </SelectContent>
                          </Select>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="category"
                    rules={{ validate: (value) => checkBrandCreateInput('category', value) }}
                    render={({ field }) => (
                      <FormItem className="flex w-full flex-col gap-[3px]">
                        <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">Categoria</FormLabel>
                        <FormControl>
                          <Select
                            value={field.value ?? undefined}
                            onValueChange={(category: EBrandCategory) => {
                              field.onChange(category);
                              if (category !== EBrandCategory.MULTIBRAND) {
                                form.setValue('relatedBrands', []);
                              }
                            }}
                          >
                            <SelectTrigger className="h-10 w-full">
                              <SelectValue placeholder="Selecione uma opção" />
                            </SelectTrigger>
                            <SelectContent className=" ">
                              {Object.values(EBrandCategory).map((category) => (
                                <SelectItem key={category} value={category}>
                                  {EBrandCategoryDisplayName[category]}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="ledColor"
                    rules={{ validate: (value) => checkBrandCreateInput('ledColor', value) }}
                    render={({ field }) => (
                      <FormItem className="flex flex-col gap-[3px]">
                        <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">
                          Código de cor - LED
                        </FormLabel>
                        <FormControl>
                          <ColorInput
                            value={field.value}
                            onChange={field.onChange}
                            mode="select"
                            options={allowedColors}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  {category === EBrandCategory.MULTIBRAND && (
                    <FormField
                      control={form.control}
                      name="relatedBrands"
                      rules={{
                        validate: (value) => {
                          const res = checkBrandCreateInput('relatedBrands', value ?? [], form.getValues());
                          return res;
                        },
                      }}
                      render={({ field }) => (
                        <FormItem className="flex flex-col gap-[3px]">
                          <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">
                            Marcas Vinculadas
                          </FormLabel>
                          <FormControl>
                            <MultiSelect
                              onSearch={setSearch}
                              onSelect={(item) => {
                                field.onChange([...(field.value ?? []), item.value as string]);
                              }}
                              onRemove={(item) => field.onChange(relatedBrands?.filter((i) => i !== item.value) ?? [])}
                              items={
                                brands
                                  ?.filter((brand) => !relatedBrands?.includes(brand.uuid))
                                  .map((brand) => ({ text: brand.name, value: brand.uuid })) ?? []
                              }
                            />
                          </FormControl>

                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )}
                </CardContent>
              </Card>
            </section>
            <div className="my-5 flex grow-0 justify-center">
              <Button
                loading={createBrandMutation.isPending || isUploading}
                type="submit"
                className="bg-primary hover:bg-primary flex grow cursor-pointer items-center gap-2.5 rounded-[5px] px-12 py-2.5 font-semibold text-white sm:w-auto sm:w-fit sm:grow-0"
              >
                <Icon name="label" fill={1} color="#fff" size={16} />
                <span className="[font-family:'Figtree'] text-sm">Salvar marca</span>
              </Button>
            </div>
          </div>
        </form>
      </Form>
    </BaseLayout>
  );
}
